/**
 * Synthetic examples of Phase 2b's session routes (CS r5a deliverables 144, 145 and 151), set in
 * "The Drowned Lanterns": session 3, "The Sunken Chapel", prepped, with the run sheet `demo@p2b`
 * gives it (CS r5b): the Flood scene of A History of Vesk, the SRD Ghoul, Maelis Dorn's secret and
 * a note; Tamsin Reed at the table; a loot capture (the capture writes take session 2, played,
 * since a prepped session's captures are not writable). `demo@p2b` (CS r5b) seeds every `p2b` row an
 * example names, so no example skips its success for it any more ({@link SEEDED_BY_P2B} stays
 * exported, unused, as `SEEDED_BY_P2A` does).
 */
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
import { EXAMPLE_SECRET, EXAMPLE_SESSION, EXAMPLE_SESSION_PLAYED } from './examples-ledger.js';
const ids = EXAMPLE_IDS;
const answer = EXAMPLE_ANSWER_IDS;
/**
 * The success skip an example naming a row of `demo@p2b` carried until CS r5b seeded the layer (CS
 * r5a deliverable 151); no route uses it since.
 */
export const SEEDED_BY_P2B = { skip: 'seeded by demo@p2b (CS r5b)' };
/**
 * `sessions.end`'s success skip: it needs a running session, and no demo layer holds one (a
 * session runs only after `sessions.start`, which the sweep's own clone does not chain).
 */
export const NEEDS_RUNNING_SESSION = {
    skip: 'needs a running session; no demo layer holds one',
};
const ref = (id, fid, title, typeKey, library = 'canon') => ({ id, fid, title, titlePending: false, typeKey, library, isStub: false });
const HISTORY = ref(ids.loreHistoryOfVesk.id, 'HistoryOfVesk001', 'A History of Vesk', 'lore');
const GHOUL = ref(ids.referenceGhoul.id, 'SrdGhoul00000001', 'Ghoul', 'ref_creature', 'reference');
const MAELIS = ref(ids.npcMaelisDorn.id, 'MaelisDornNpc001', 'Maelis Dorn', 'npc');
const TAMSIN = ref(ids.pcTamsinReed.id, 'TamsinReedPc0001', 'Tamsin Reed', 'pc');
const OLD_QUAY = ref(ids.locationOldQuay.id, 'OldQuayPlace0001', 'the Old Quay', 'location');
const TIDEBELL = ref(ids.itemTidebell.id, 'TidebellItem0001', 'the Tidebell', 'item');
const SESSION_2 = ref(ids.sessionSaltInTheWells.id, 'SessionSalt00002', 'Salt in the Wells', 'session');
const session3 = ids.sessionSunkenChapel.id;
/** The Flood, a scene of session 3's run sheet: first, thirty minutes planned. */
export const EXAMPLE_SESSION_ITEM = {
    id: ids.sessionItemFloodScene.id,
    rv: 1,
    sessionId: session3,
    kind: 'scene',
    documentId: ids.loreHistoryOfVesk.id,
    blockId: ids.blockHistoryFloodHeading.id,
    secretId: null,
    document: HISTORY,
    position: 'a0',
    label: 'The Flood',
    plannedNote: 'Open with the bells under the water.',
    estMinutes: 30,
    pinned: false,
    playState: 'unplayed',
    outcomeNote: null,
    anchorLost: false,
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/** The SRD Ghoul on the sheet: a reference document, which an item may name (`target-ok`). */
export const EXAMPLE_SESSION_ITEM_CREATED = {
    ...EXAMPLE_SESSION_ITEM,
    id: answer.sessionItemCreated,
    kind: 'document',
    documentId: ids.referenceGhoul.id,
    blockId: null,
    document: GHOUL,
    position: 'a2',
    label: 'Ghouls in the crypt',
    plannedNote: null,
    estMinutes: 20,
};
/** Maelis Dorn's secret: `document` and `blockId` filled from the secret (DECISIONS 219 ruling 1). */
const SECRET_ITEM = {
    ...EXAMPLE_SESSION_ITEM,
    id: answer.sessionItemSecret,
    kind: 'secret',
    documentId: null,
    blockId: EXAMPLE_SECRET.blockId,
    secretId: EXAMPLE_SECRET.id,
    document: MAELIS,
    position: 'a3',
    label: 'Her ledger',
    plannedNote: 'Only if they search the counting room.',
    estMinutes: null,
    pinned: true,
};
/** A note: a break, ten minutes. */
export const EXAMPLE_SESSION_ITEM_NOTE = {
    ...EXAMPLE_SESSION_ITEM,
    id: ids.sessionItemChapelNote.id,
    kind: 'note',
    documentId: null,
    blockId: null,
    document: null,
    position: 'a4',
    label: 'Break',
    plannedNote: null,
    estMinutes: 10,
};
/** Session 3's run sheet, in position order. */
export const EXAMPLE_SESSION_ITEMS = [
    EXAMPLE_SESSION_ITEM,
    EXAMPLE_SESSION_ITEM_CREATED,
    SECRET_ITEM,
    EXAMPLE_SESSION_ITEM_NOTE,
];
/** `sessions.items.update`'s answer: the scene's planned note and minutes changed. */
export const EXAMPLE_SESSION_ITEM_UPDATED = {
    ...EXAMPLE_SESSION_ITEM,
    rv: 2,
    plannedNote: 'Open on the bells, then the flood.',
    estMinutes: 35,
};
/** `sessions.items.move`'s answer: the note moved first. */
export const EXAMPLE_SESSION_ITEM_MOVED = {
    ...EXAMPLE_SESSION_ITEM_NOTE,
    rv: 2,
    position: 'Zz',
};
/** Tamsin Reed at session 3. */
export const EXAMPLE_SESSION_ATTENDANCE = {
    sessionId: session3,
    rows: [{ pc: TAMSIN, present: true, rv: 1 }],
};
/** A loot capture of session 3. */
export const EXAMPLE_SESSION_CAPTURE = {
    id: ids.captureChapelLoot.id,
    rv: 1,
    sessionId: session3,
    seq: 1,
    kind: 'loot',
    body: 'A silver bell rope, cut clean.',
    docIds: [ids.itemTidebell.id],
    at: null,
    atReal: EXAMPLE_NOW,
    status: 'open',
    resolution: null,
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/** `sessions.captures.create`'s answer: a decision captured in session 2's After. */
export const EXAMPLE_SESSION_CAPTURE_CREATED = {
    ...EXAMPLE_SESSION_CAPTURE,
    id: answer.sessionCaptureCreated,
    sessionId: ids.sessionSaltInTheWells.id,
    seq: 4,
    kind: 'decision',
    body: 'The party sided with the well-keepers.',
    docIds: [ids.itemTidebell.id],
};
/**
 * `sessions.captures.update`'s answer: session 2's open stub (played, so its captures are writable)
 * given the Tidebell (DECISIONS 490).
 */
export const EXAMPLE_SESSION_CAPTURE_UPDATED = {
    ...EXAMPLE_SESSION_CAPTURE,
    id: ids.captureSaltStub.id,
    rv: 2,
    sessionId: ids.sessionSaltInTheWells.id,
    seq: 1,
    kind: 'stub',
    body: 'The ferryman who saw the Tidebell go under.',
    docIds: [ids.itemTidebell.id],
};
/** `sessions.start`'s answer: session 3 running, its number kept. */
export const EXAMPLE_SESSION_STARTED = {
    ...EXAMPLE_SESSION,
    status: 'running',
    startedAt: EXAMPLE_NOW,
};
/** `sessions.end`'s answer: session 3 played. */
export const EXAMPLE_SESSION_ENDED = {
    ...EXAMPLE_SESSION_STARTED,
    status: 'played',
    endedAt: '2026-09-12T23:30:00.000Z',
};
/** `sessions.finish`'s answer: session 2 processed, and the next session planned at NOW. */
export const EXAMPLE_SESSION_FINISHED = {
    session: { ...EXAMPLE_SESSION_PLAYED, status: 'processed' },
    next: {
        ...EXAMPLE_SESSION,
        document: ref(answer.sessionNext, 'SessionNext00004', 'Session 4', 'session'),
        number: null,
        realDate: null,
        status: 'planned',
        placement: EXAMPLE_SESSION.placement === null
            ? null
            : {
                ...EXAMPLE_SESSION.placement,
                id: answer.placementSessionNext,
                documentId: answer.sessionNext,
            },
    },
};
/** Tonight for session 3: Last time from session 2, the run sheet, and what it suggests. */
export const EXAMPLE_TONIGHT = {
    session: EXAMPLE_SESSION,
    nextNumber: 4,
    lastTime: {
        session: SESSION_2,
        number: 2,
        recapHtml: '<h2>Recap</h2><p>The wells ran salt; the party sided with the well-keepers.</p>',
    },
    strongStart: {
        blockId: answer.strongStartBlock,
        html: '<h2>Strong start</h2><p>The chapel bell rings from under the water.</p>',
    },
    characters: [{ pc: TAMSIN, present: true }],
    items: [...EXAMPLE_SESSION_ITEMS],
    plannedMinutes: 60,
    secretsReady: [EXAMPLE_SECRET],
    suggestions: {
        npcs: [{ doc: MAELIS, via: 'mention' }],
        monsters: [{ doc: GHOUL, via: 'embed' }],
        locations: [{ doc: OLD_QUAY, via: 'link' }],
        loot: [{ doc: TIDEBELL, via: 'mention' }],
    },
};
/** A refusal of the session's status (409 `session_state`), with the statuses that allow it. */
const sessionState = (status, allowed) => ({
    code: 'session_state',
    message: 'The session’s status does not allow this.',
    details: { status, allowed: [...allowed] },
});
/**
 * The 409 `session_state` answers, one example each (which status allows which write is 2.C's;
 * these are its likely answers): an item added to session 1, processed; a start of session 2,
 * played. A client reads `code` and `details`, never `message`.
 */
export const EXAMPLE_SESSION_2B_REFUSALS = {
    'sessions.items.create': [
        {
            status: 409,
            body: sessionState('processed', ['planned', 'prepped', 'running', 'played']),
            request: {
                params: { id: ids.sessionLampsOutOnTheQuay.id },
                body: { kind: 'note', label: 'Too late' },
            },
        },
    ],
    'sessions.start': [
        {
            status: 409,
            body: sessionState('played', ['planned', 'prepped']),
            request: { params: { id: ids.sessionSaltInTheWells.id }, body: {} },
        },
    ],
};
//# sourceMappingURL=examples-sessions.js.map